import { useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

/**
 * T-55: the muted card the software factory's question and suggestion rounds sit in (as on the
 * factory's own board). With `fold` (a settled round: answered or decided) it starts folded to its
 * header line, which opens it on a click; without, it's always open. `id` lets a pointer in the
 * comments scroll to it (and open it, `showFoldingCard` in ./folding-card).
 */
export function FoldingCard({
  header,
  summary,
  fold = false,
  id,
  className,
  children,
}: {
  /** The header line: icon, what it is, a badge, and who and when (ml-auto). */
  header: ReactNode;
  /** A line under the header while it's folded (what was answered or decided), so it reads without opening. */
  summary?: ReactNode;
  /** Starts folded to the header, which toggles it. */
  fold?: boolean;
  id?: string;
  className?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const headerClass = 'flex w-full flex-wrap items-center gap-x-2 gap-y-1 p-3 text-left text-xs font-medium text-muted-foreground';
  return (
    <Collapsible
      id={id}
      open={!fold || open}
      onOpenChange={setOpen}
      className={cn('scroll-mt-4 rounded-md border bg-muted/40', className)}
    >
      {fold ? (
        <CollapsibleTrigger className={cn(headerClass, 'rounded-md hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring')}>
          <ChevronRight className={cn('size-3.5 shrink-0 transition-transform', open && 'rotate-90')} aria-hidden />
          {header}
          {summary && !open ? (
            <span className="line-clamp-2 basis-full pl-5.5 text-left font-normal text-foreground/80">{summary}</span>
          ) : null}
        </CollapsibleTrigger>
      ) : (
        <div className={headerClass}>{header}</div>
      )}
      <CollapsibleContent className="space-y-3 px-3 pb-3">{children}</CollapsibleContent>
    </Collapsible>
  );
}
